import type { FC } from 'react';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { CheckOutline } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { ensureHtml, stripHtmlToPlainText } from '@shared/utils/html';
import { withWindow } from '@shared/utils/withWindow';
import { LazyDescriptionRichTextEditor } from '@shared/components/DescriptionRichTextEditor/DescriptionRichTextEditor.lazy';
import i18n from '@shared/core/i18n/i18n';
import type { Jodit } from '@shared/core/jodit-react-ssr';
import { usePageNoteAutosave } from './usePageNoteAutosave';
import { ClampedNotePreview, CollapsedNoteBox, COLLAPSED_NOTE_HEIGHT, EXPANDED_NOTE_MAX_HEIGHT, PageNoteEditorWrapper } from './PageNoteField.styles.internal';

type SaveStatus = 'idle' | 'saving' | 'saved';

// Jodit draws link and dialog popups in a portal, so focus there still counts as editing.
const JODIT_PORTAL_SELECTOR = '.jodit-popup, .jodit-dialog, .jodit-popup-container';

// The editor subtree's namespaces. `useTranslation` suspends on one that is not loaded yet.
const EDITOR_NAMESPACES = ['richTextEditor', 'hotelSuggestionDialog'];

type PageNoteFieldProps = Readonly<{
  /** The saved note, rich-text HTML or legacy plain text. */
  note: string;
  isDisabled: boolean;
  /** Persists the note. Resolves false when the write failed. */
  onSave: (html: string) => Promise<boolean>;
  /** Runs once focus leaves the field, after the final save. */
  onEditingEnd: () => void;
}>;

/**
 * "Note to Guests" as a real field rather than a button that opens a dialog: click it and type,
 * and it saves itself. At rest it is a plain two-line box; the editor takes its place while the
 * field has focus and hides again on blur or Escape.
 */
export const PageNoteField: FC<PageNoteFieldProps> = ({ note, isDisabled, onSave, onEditingEnd }) => {
  const { tq } = useTranslation('accommodationsAdmin');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const joditRef = useRef<Jodit | null>(null);
  const collapseTimeoutRef = useRef<number | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  // Mounted on the first click, then kept. Unmounting re-injects the editor's global stylesheet,
  // which restyles the whole document.
  const [hasMountedEditor, setHasMountedEditor] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  // What this field shows. It leads the `note` prop between a keystroke and the refetch.
  const [noteHtml, setNoteHtml] = useState(() => ensureHtml(note));
  const [editorKey, setEditorKey] = useState(0);
  const draftRef = useRef(noteHtml);
  // `saveStatus` cannot drive the refetch: a blur before the debounce reads it in the same tick.
  const hasSavedRef = useRef(false);

  const placeholder = tq`You can share a note with your guests here!`;

  const handlePersist = useEventCallback(async (html: string) => {
    setSaveStatus('saving');
    const didSave = await onSave(html);
    if (didSave) {
      hasSavedRef.current = true;
    }
    setSaveStatus(didSave ? 'saved' : 'idle');
    return didSave;
  });

  const { handleChange, flush, resetBaseline, isSettled } = usePageNoteAutosave({
    savedNote: noteHtml,
    onPersist: handlePersist,
  });

  // Fetch the chunk and its strings up front, so the first click mounts a ready editor.
  useEffect(() => {
    if (!isDisabled) {
      LazyDescriptionRichTextEditor.preload();
      void i18n.loadNamespaces(EDITOR_NAMESPACES).catch(() => undefined);
    }
  }, [isDisabled]);

  // A note written elsewhere (the preview iframe). The editor is uncontrolled, so rebuild it.
  // Skipped while a write is pending: the prop still holds the pre-save value and would flash back.
  useEffect(() => {
    if (!isEditing && isSettled()) {
      const incoming = ensureHtml(note);
      if (incoming !== draftRef.current) {
        draftRef.current = incoming;
        resetBaseline(incoming);
        setNoteHtml(incoming);
        setEditorKey(key => key + 1);
      }
    }
  }, [note, isEditing, resetBaseline, isSettled]);

  const handleEditorChange = useEventCallback((html: string) => {
    draftRef.current = html;
    handleChange(html);
  });

  // Caret at the end, so the field opens like any other. Driven by `isEditing` as well as by the
  // instance arriving, because the editor is revealed rather than remounted.
  const focusEditor = useEventCallback(() => {
    const editor = joditRef.current;
    if (!isEditing || !editor?.editor) {
      return;
    }
    editor.s.focus();
    editor.s.setCursorIn(editor.editor, false);
  });

  const handleEditorRef = useEventCallback((editor: Jodit) => {
    joditRef.current = editor;
    focusEditor();
  });

  useEffect(focusEditor, [isEditing, focusEditor]);
  useEffect(() => () => withWindow(win => win.clearTimeout(collapseTimeoutRef.current)), []);

  const stopEditing = useEventCallback(() => {
    setNoteHtml(draftRef.current);
    setIsEditing(false);
    // The mutation does not return `note` and the provider reads it cache-only, so the re-read
    // has to land after the last write settles.
    void flush().then(() => {
      if (hasSavedRef.current) {
        hasSavedRef.current = false;
        onEditingEnd();
      }
    });
  });

  /**
   * Collapse checks always run a tick late. Reading `relatedTarget` is no good — a toolbar button
   * reports no new focus — and collapsing inside the event would hide the editor while Jodit is
   * still handling that same event, which throws on its now-detached selection.
   */
  const scheduleCollapseCheck = useEventCallback(() => {
    withWindow(win => {
      win.clearTimeout(collapseTimeoutRef.current);
      collapseTimeoutRef.current = win.setTimeout(() => {
        const active = win.document.activeElement as HTMLElement | null;
        if (containerRef.current?.contains(active) || active?.closest?.(JODIT_PORTAL_SELECTOR)) {
          return;
        }
        stopEditing();
      }, 0);
    });
  });

  // Escape leaves the field like clicking away. Jodit owns the keys inside, so this sits outside.
  const handleContainerKeyDown = useEventCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    withWindow(win => (win.document.activeElement as HTMLElement | null)?.blur());
    scheduleCollapseCheck();
  });

  // Focus can leave a Jodit portal without the container ever seeing a second blur, so watch the
  // document as well while editing.
  useEffect(() => {
    if (!isEditing) {
      return undefined;
    }
    return withWindow(win => {
      win.document.addEventListener('mousedown', scheduleCollapseCheck);
      win.document.addEventListener('focusin', scheduleCollapseCheck);
      return () => {
        win.document.removeEventListener('mousedown', scheduleCollapseCheck);
        win.document.removeEventListener('focusin', scheduleCollapseCheck);
      };
    }, undefined);
  }, [isEditing, scheduleCollapseCheck]);

  const handleStartEditing = useEventCallback(() => {
    if (isDisabled) {
      return;
    }
    setHasMountedEditor(true);
    setIsEditing(true);
  });

  const previewText = stripHtmlToPlainText(noteHtml).replace(/\s+/g, ' ').trim();

  const restingBox = (
    <CollapsedNoteBox
      as="button"
      width="100%"
      type="button"
      textAlign="left"
      border="1px solid"
      borderColor="inputBorder"
      minHeight={pxToRem(COLLAPSED_NOTE_HEIGHT)}
      typographyVariant="body2"
      borderRadius="field"
      // Native `disabled`, not `aria-disabled`: the field contract wants no ring when disabled,
      // and an aria-only button still takes tab focus and paints one.
      disabled={isDisabled}
      onClick={handleStartEditing}
      cursor={isDisabled ? 'not-allowed' : 'text'}
    >
      {previewText ? <ClampedNotePreview lineClamp={2}>{previewText}</ClampedNotePreview> : <Box color="mono8">{placeholder}</Box>}
    </CollapsedNoteBox>
  );

  return (
    <Box width="100%">
      {!isEditing && restingBox}
      {hasMountedEditor && (
        <PageNoteEditorWrapper
          ref={containerRef}
          onBlur={scheduleCollapseCheck}
          onKeyDown={handleContainerKeyDown}
          // Hidden rather than unmounted — see `hasMountedEditor`.
          display={isEditing ? 'block' : 'none'}
        >
          {/* Our own boundary: a late namespace hides this field, not the root one above us. */}
          <Suspense fallback={restingBox}>
            <LazyDescriptionRichTextEditor
              key={editorKey}
              // The resting box doubles as the loading state, so the swap is invisible.
              fallback={restingBox}
              content={noteHtml}
              placeholder={placeholder}
              onChange={handleEditorChange}
              editorRef={handleEditorRef}
              resizable={false}
              autoGrow
              autoGrowMaxHeight={pxToRem(EXPANDED_NOTE_MAX_HEIGHT)}
              minHeight={pxToRem(COLLAPSED_NOTE_HEIGHT)}
            />
          </Suspense>
        </PageNoteEditorWrapper>
      )}
      {/* At rest the field shows nothing below it, so the line only survives an in-flight save. */}
      {(isEditing || saveStatus === 'saving') && (
        <Flex
          gap={3}
          alignItems="center"
          justifyContent="space-between"
          marginTop={2}
        >
          <TextV2
            typographyVariant="label2"
            color="mono10"
          >
            {isEditing ? tq`Press Esc to finish editing` : ''}
          </TextV2>
          {saveStatus !== 'idle' && (
            <Flex
              gap={1}
              alignItems="center"
              flexShrink={0}
            >
              {saveStatus === 'saved' && (
                <CheckOutline
                  size={16}
                  color="mono10"
                />
              )}
              <TextV2
                typographyVariant="label2"
                color="mono10"
              >
                {saveStatus === 'saving' ? tq`Saving…` : tq`Saved`}
              </TextV2>
            </Flex>
          )}
        </Flex>
      )}
    </Box>
  );
};

PageNoteField.displayName = 'PageNoteField';
